// vendor
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  from,
  ServerError,
  Observable,
  fromPromise,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';

// utils
import storageService from '@/lib/storage-service';
import { postData } from '@/lib/axios';

// constants
import {
  LOGIN_ENDPOINT,
  REFRESH_TOKEN_ENDPOINT,
} from '@/constants/api-endpoints.constants';
import { ACCESS_TOKEN } from '@/constants/storage.constants';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

// Create an HttpLink for the GraphQL endpoint
const httpLink = new HttpLink({
  uri: `${import.meta.env.VITE_API_GATEWAY_BASE_URL}/emr/fhir/R4/$graphql`,
});

// Create an authLink to add Authorization header to each request
const authLink = new ApolloLink((operation, forward) => {
  const token = cookies.get(ACCESS_TOKEN);
  if (token) {
    operation.setContext({
      headers: {
        ...operation.getContext().headers,
        Authorization: `Bearer ${token}`, // Add the Authorization header
        Accept: 'application/fhir+json',
      },
    });
  }
  return forward(operation);
});

// Create errorLink for handling token expiration and refresh logic
const errorLink = onError(({ networkError, operation, forward }) => {
  if (networkError && (networkError as ServerError).statusCode === 401) {
    const originalRequest = operation;
    return;

    if (originalRequest.operationName === LOGIN_ENDPOINT) {
      return;
    }

    const refreshToken = storageService.getRefreshToken();

    if (!refreshToken) {
      storageService.clearToken();
      window.location.assign('/');
      return;
    }

    return fromPromise(
      postData(REFRESH_TOKEN_ENDPOINT, { refresh: refreshToken })
        .then((data) => {
          if (data.access) {
            storageService.setAccessToken(data.access);
            originalRequest.setContext({
              headers: {
                ...originalRequest.getContext().headers,
                Authorization: `Bearer ${data.access}`,
              },
            });
            return forward(originalRequest);
          } else {
            storageService.clearToken();
            window.location.assign('/');
            return;
          }
        })
        .catch((error) => {
          storageService.clearToken();
          window.location.assign('/');
          throw error;
        })
    ).flatMap((observable) => (observable ? observable : Observable.of()));
  }
});

// Combine links and create Apollo Client
const client = new ApolloClient({
  link: from([authLink, errorLink, httpLink]), // Combine links
  cache: new InMemoryCache(),
});

export default client;
